import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useReducer } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { closeCallOutcome, CallOutcomePrompt } from 'src/store/slices/app/appSlice';
import { CallListPersonType, CallListEntryStatus } from 'src/services/call-lists/call-lists.types';
import {
  useLogCallMutation,
  useGetCallListEntryQuery,
  useGetCallListMembershipsQuery,
} from 'src/services/call-lists/call-lists.service';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { personName } from '../utils';
import { useCallTimer } from '../hooks/use-call-timer';
import CallOutcomeForm from '../components/call-outcome-form';
import { useCallListsEnabled } from '../hooks/use-call-lists-enabled';
import { canSaveCall, buildLogCall, dialledNumberFor } from '../call-outcome';
import { initialCallState, currentCallReducer } from '../hooks/use-current-call';

// ----------------------------------------------------------------------

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Logging a call placed from a profile, quick view or list row (JT1-1827). It opens when the person
 * is on one of the caller's active call lists and records the attempt there, as the call list's own
 * panel would. Otherwise it closes without showing anything.
 */
export default function CallOutcomeOverlay() {
  const prompt = useAppSelector((state) => state.app.callOutcomePrompt);
  if (!prompt) return null;
  // Each call starts a clean form.
  return <CallOutcomeDialog key={`${prompt.personId}:${prompt.startedAt}`} prompt={prompt} />;
}

function CallOutcomeDialog({ prompt }: { prompt: CallOutcomePrompt }) {
  const { t } = useTranslate();
  const dispatchApp = useAppDispatch();
  const enabled = useCallListsEnabled();
  const confirmClose = useBoolean();

  const { currentData: memberships, isError: membershipsFailed } = useGetCallListMembershipsQuery(
    { entityType: prompt.personType as CallListPersonType, entityId: prompt.personId },
    { skip: !enabled }
  );

  // The caller's active lists with this person, the one due soonest first.
  const active = useMemo(() => {
    const at = (value: string | null) => (value ? new Date(value).getTime() : 0);
    return (memberships?.mine ?? [])
      .filter((membership) => membership.status === CallListEntryStatus.ACTIVE)
      .sort((a, b) => at(a.next_call_at) - at(b.next_call_at));
  }, [memberships]);

  const [chosenEntryId, setChosenEntryId] = useState<string | null>(null);
  const membership = active.find((item) => item.entry_id === chosenEntryId) ?? active[0] ?? null;

  const { currentData: entry } = useGetCallListEntryQuery(
    { listId: membership?.call_list_id as string, entryId: membership?.entry_id as string },
    { skip: !membership }
  );

  const [state, dispatch] = useReducer(currentCallReducer, prompt.startedAt, (startedAt) => ({
    ...initialCallState(),
    phase: 'on_call' as const,
    startedAt,
  }));
  const seconds = useCallTimer(state.startedAt, state.endedAt, state.phase === 'on_call');

  const [logCall] = useLogCallMutation();

  // Not on any of the caller's call lists: nothing to log, so the call carries on as before.
  const nothingToLog = !enabled || membershipsFailed || (!!memberships && !active.length);
  useEffect(() => {
    if (nothingToLog) dispatchApp(closeCallOutcome());
  }, [nothingToLog, dispatchApp]);

  if (nothingToLog || !membership) return null;

  const close = () => dispatchApp(closeCallOutcome());

  const save = async () => {
    if (!entry || !state.outcome) return;
    dispatch({ type: 'SAVE' });
    try {
      const result = await logCall({
        listId: membership.call_list_id,
        entryId: membership.entry_id,
        data: buildLogCall(entry, state, dialledNumberFor(entry, prompt.dialledNumber)),
      }).unwrap();
      enqueueSnackbar(
        result.note_saved ? t('call_lists.toasts.logged') : t('call_lists.toasts.logged_without_note'),
        { variant: result.note_saved ? 'success' : 'warning' }
      );
      close();
    } catch (error: any) {
      dispatch({ type: 'SAVE_FAIL', error: error?.data?.message || t('call_lists.toasts.log_failed') });
    }
  };

  const chooseList = (entryId: string) => {
    setChosenEntryId(entryId);
    // Each list has its own next-call defaults.
    dispatch({ type: 'SET_NEXT_CALL', nextCall: null });
  };

  const name = prompt.name || (entry ? personName(entry.person) : '');
  const canLog = !!entry && entry.status === CallListEntryStatus.ACTIVE;

  return (
    <>
      <Dialog open fullWidth maxWidth="sm" onClose={confirmClose.onTrue} data-testid="call-outcome-overlay">
        <DialogTitle>{name ? t('call_lists.overlay.title', { name }) : t('call_lists.overlay.title_no_name')}</DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {active.length > 1 ? (
              <TextField
                select
                size="small"
                label={t('call_lists.overlay.log_against')}
                value={membership.entry_id}
                onChange={(event) => chooseList(event.target.value)}
                disabled={state.phase === 'saving'}
                data-testid="call-outcome-overlay-list"
              >
                {active.map((item) => (
                  <MenuItem key={item.entry_id} value={item.entry_id}>
                    {`${item.call_list_name} · ${t('call_lists.attempt_of', { current: item.attempt_number, max: item.max_attempts })}`}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {`${membership.call_list_name} · ${t('call_lists.attempt_of', { current: membership.attempt_number, max: membership.max_attempts })}`}
              </Typography>
            )}

            {state.phase === 'on_call' && (
              <Alert
                severity="info"
                icon={<Iconify icon="solar:phone-calling-bold" />}
                data-testid="call-outcome-overlay-on-call"
                action={(
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    startIcon={<Iconify icon="solar:end-call-bold" />}
                    onClick={() => dispatch({ type: 'END_CALL', at: Date.now() })}
                  >
                    {t('call_lists.actions.call_ended')}
                  </Button>
                )}
              >
                {t('call_lists.overlay.on_call', { duration: `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}` })}
              </Alert>
            )}

            {!entry && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {entry && !canLog && <Alert severity="warning">{t(`call_lists.panel.ended.${entry.status}`)}</Alert>}

            {entry && canLog && <CallOutcomeForm entry={entry} state={state} dispatch={dispatch} seconds={seconds} />}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={confirmClose.onTrue} disabled={state.phase === 'saving'}>
            {t('call_lists.overlay.not_now')}
          </Button>
          <LoadingButton
            variant="contained"
            loading={state.phase === 'saving'}
            disabled={!entry || !canLog || !canSaveCall(entry, state)}
            onClick={save}
            data-testid="call-outcome-overlay-log"
          >
            {t('call_lists.actions.log')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmClose.value}
        onClose={confirmClose.onFalse}
        title={t('call_lists.overlay.discard_title')}
        content={t('call_lists.overlay.discard_content', { list: membership.call_list_name })}
        action={(
          <Button variant="contained" color="error" onClick={close} data-testid="call-outcome-overlay-discard">
            {t('call_lists.overlay.discard')}
          </Button>
        )}
      />
    </>
  );
}
